import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useMotionTemplate,
  useTransform,
  useSpring,
  useAnimationFrame,
  useReducedMotion,
  animate,
} from 'framer-motion';
import { useEffect, useRef, useState, useCallback, memo } from 'react';
import {
  ChartBarIcon,
  HeartIcon,
  BoltIcon,
  ShieldCheckIcon,
  SparklesIcon,
  SunIcon,
  MoonIcon,
  LinkIcon,
  ArrowTrendingUpIcon,
  Bars3Icon,
  XMarkIcon,
  CursorArrowRaysIcon,
  RocketLaunchIcon,
  ChatBubbleOvalLeftIcon,
  BellIcon,
  CurrencyDollarIcon,
  FilmIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid, PlayIcon } from '@heroicons/react/24/solid';
import { FaYoutube, FaTiktok, FaInstagram, FaXTwitter, FaTwitch, FaFacebook } from 'react-icons/fa6';
import { toggleTheme } from '../store/slices/themeSlice';
import BrandLogo, { brandAsset } from '../components/shared/BrandLogo';

// ── Data ─────────────────────────────────────────────────────────────────────
const PLATFORM_ICONS = [
  { icon: FaYoutube, label: 'YouTube', color: '#FF0000', bg: 'bg-red-500/10', text: 'text-red-500', glow: 'shadow-red-500/20' },
  { icon: FaTiktok, label: 'TikTok', color: '#69C9D0', bg: 'bg-cyan-500/10', text: 'text-cyan-500 dark:text-cyan-400', glow: 'shadow-cyan-500/20' },
  { icon: FaInstagram, label: 'Instagram', color: '#E4405F', bg: 'bg-pink-500/10', text: 'text-pink-500', glow: 'shadow-pink-500/20' },
  { icon: FaFacebook, label: 'Facebook', color: '#1877F2', bg: 'bg-blue-500/10', text: 'text-blue-500', glow: 'shadow-blue-500/20' },
  { icon: FaTwitch, label: 'Twitch', color: '#9146FF', bg: 'bg-purple-500/10', text: 'text-purple-500', glow: 'shadow-purple-500/20' },
  { icon: FaXTwitter, label: 'X (Twitter)', color: '#1DA1F2', bg: 'bg-sky-500/10', text: 'text-sky-500', glow: 'shadow-sky-500/20' },
];

const ROTATING_WORDS = [
  { text: 'Connect', gradient: 'from-red-500 via-pink-500 to-purple-500' },
  { text: 'Grow', gradient: 'from-pink-500 via-fuchsia-500 to-violet-500' },
  { text: 'Earn', gradient: 'from-yellow-400 via-orange-500 to-red-500' },
  { text: 'Create', gradient: 'from-blue-500 via-indigo-500 to-purple-500' },
  { text: 'Thrive', gradient: 'from-emerald-400 via-cyan-400 to-sky-500' },
];

const MARQUEE_ITEMS = [
  '500+ Creators', '15K+ Videos Managed', '1M+ Data Points', '6 Platforms Connected',
  '99.9% Uptime', '4 AI Models', 'Real-Time Analytics', 'Enterprise Security',
  'Auto Revenue Tracking', 'Smart Anomaly Detection',
];

const TESTIMONIALS = [
  {
    name: 'Sarah Chen', handle: '@sarahcreates', avatar: 'SC',
    quote: 'OmniConnX completely transformed how I manage my channels. The AI analytics helped me grow 3x in 6 months. I can\'t imagine going back.',
    platform: 'YouTube', platformIcon: FaYoutube, platformColor: 'text-red-500', gradient: 'from-red-500 to-orange-500',
  },
  {
    name: 'Marcus Rivera', handle: '@marcusvibes', avatar: 'MR',
    quote: 'Finally, one dashboard for all my platforms. The revenue tracking alone saved me hours every week. This is the future of creator tools.',
    platform: 'TikTok', platformIcon: FaTiktok, platformColor: 'text-cyan-500', gradient: 'from-cyan-400 to-blue-500',
  },
  {
    name: 'Priya Sharma', handle: '@priyatalks', avatar: 'PS',
    quote: 'The health monitoring caught a subscriber drop before I even noticed. The AI suggestions helped me recover in days, not weeks.',
    platform: 'Instagram', platformIcon: FaInstagram, platformColor: 'text-pink-500', gradient: 'from-pink-500 to-purple-500',
  },
];

const CREATOR_CARDS = [
  {
    name: 'Jake Studios',
    handle: '@jakestudios',
    avatar: 'JS',
    bio: 'Gaming content & live streams',
    followers: '1.2M',
    platform: 'YouTube',
    platformIcon: FaYoutube,
    gradient: 'from-red-500 to-rose-600',
    accent: '#ef4444',
    ctaLabel: 'Subscribe',
    battery: '87',
    stats: [{ label: 'Subs', value: '1.2M' }, { label: 'Views', value: '89M' }, { label: 'Watch hrs', value: '4.2M' }],
    backStats: [
      { label: 'Avg Views', value: '245K', trend: '+22%' },
      { label: 'CTR', value: '8.4%', trend: '+1.2%' },
      { label: 'Revenue', value: '$18K/mo', trend: '+15%' },
      { label: 'Watch Time', value: '12min', trend: '+3min' },
    ],
  },
  {
    name: 'Mia Rose',
    handle: '@miarose',
    avatar: 'MR',
    bio: 'Fashion & lifestyle creator',
    followers: '892K',
    platform: 'Instagram',
    platformIcon: FaInstagram,
    gradient: 'from-purple-500 via-pink-500 to-orange-400',
    accent: '#d946ef',
    ctaLabel: 'Follow',
    battery: '92',
    stats: [{ label: 'Followers', value: '892K' }, { label: 'Posts', value: '2.1K' }, { label: 'Reach', value: '3.4M' }],
    backStats: [
      { label: 'Avg Reach', value: '340K', trend: '+24%' },
      { label: 'Engagement', value: '11.5%', trend: '+3.4%' },
      { label: 'Story Views', value: '125K', trend: '+18%' },
      { label: 'Revenue', value: '$8.5K/mo', trend: '+28%' },
    ],
  },
  {
    name: 'Kai Rhythm',
    handle: '@kairhythm',
    avatar: 'KR',
    bio: 'Dance & music entertainment',
    followers: '2.8M',
    platform: 'TikTok',
    platformIcon: FaTiktok,
    gradient: 'from-cyan-400 to-pink-500',
    accent: '#22d3ee',
    ctaLabel: 'Follow',
    battery: '78',
    stats: [{ label: 'Followers', value: '2.8M' }, { label: 'Likes', value: '45M' }, { label: 'Videos', value: '890' }],
    backStats: [
      { label: 'Avg Views', value: '1.2M', trend: '+45%' },
      { label: 'Engagement', value: '14.2%', trend: '+5.1%' },
      { label: 'Shares', value: '890K', trend: '+31%' },
      { label: 'Revenue', value: '$22K/mo', trend: '+52%' },
    ],
  },
];

// ── Animated Counter ─────────────────────────────────────────────────────────
const AnimatedCounter = ({ target, prefix = '', suffix = '', duration = 2 }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => `${prefix}${Math.round(v).toLocaleString()}${suffix}`);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (inView) animate(count, target, { duration });
  }, [inView, count, target, duration]);

  return <motion.span ref={ref}>{rounded}</motion.span>;
};

// ── Rotating Word (Hero) ─────────────────────────────────────────────────────
const RotatingWord = () => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setIndex((i) => (i + 1) % ROTATING_WORDS.length), 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="inline-block relative h-[1.3em] overflow-hidden align-bottom" style={{ minWidth: '5em' }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={ROTATING_WORDS[index].text}
          initial={{ y: 40, opacity: 0, rotateX: -45 }}
          animate={{ y: 0, opacity: 1, rotateX: 0 }}
          exit={{ y: -40, opacity: 0, rotateX: 45 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className={`absolute left-0 bg-gradient-to-r ${ROTATING_WORDS[index].gradient} bg-clip-text text-transparent`}
        >
          {ROTATING_WORDS[index].text}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

// ── Holographic Headline (per-letter 3D flip-in + shimmer) ──────────────────
const HEADLINE_TEXT = 'Where Creators';
const holoLetterVariants = {
  hidden: { opacity: 0, rotateX: -85, y: 30 },
  show: { opacity: 1, rotateX: 0, y: 0, transition: { type: 'spring', stiffness: 220, damping: 18 } },
};
const HoloHeadline = memo(() => (
  <motion.span
    className="block"
    style={{ perspective: 600, transformStyle: 'preserve-3d' }}
    initial="hidden"
    animate="show"
    variants={{ show: { transition: { staggerChildren: 0.045, delayChildren: 0.15 } } }}
    aria-label={HEADLINE_TEXT}
  >
    {[...HEADLINE_TEXT].map((ch, i) => (
      <motion.span
        key={i}
        aria-hidden="true"
        variants={holoLetterVariants}
        className="holo-ch"
        style={{ transformOrigin: '50% 100%' }}
      >
        {ch === ' ' ? ' ' : ch}
      </motion.span>
    ))}
  </motion.span>
));
HoloHeadline.displayName = 'HoloHeadline';

// ── Hero headline with self-contained reaction bursts ────────────────────────
// Bursts live here (not in the page component) so spawning them re-renders
// only this small subtree, never the whole landing page.
const HeroHeadline = () => {
  const prefersReduced = useReducedMotion();
  const titleRef = useRef(null);
  const [bursts, setBursts] = useState([]);

  const spawnBurst = useCallback((x, y, count) => {
    const items = Array.from({ length: count }, (_, i) => ({
      id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
      x: x + (Math.random() - 0.5) * 150,
      y: y + (Math.random() - 0.5) * 30,
      emoji: BURST_EMOJI[Math.floor(Math.random() * BURST_EMOJI.length)],
      rot: (Math.random() - 0.5) * 60,
    }));
    setBursts((b) => [...b, ...items]);
    const ids = new Set(items.map((it) => it.id));
    setTimeout(() => setBursts((b) => b.filter((it) => !ids.has(it.id))), 1800);
  }, []);

  useEffect(() => {
    if (prefersReduced) return undefined;
    const id = setInterval(() => {
      const el = titleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      spawnBurst(rect.width * (0.1 + Math.random() * 0.7), rect.height * (0.2 + Math.random() * 0.5), 2);
    }, 2600);
    return () => clearInterval(id);
  }, [prefersReduced, spawnBurst]);

  const handleClick = useCallback((e) => {
    const rect = titleRef.current?.getBoundingClientRect();
    if (!rect) return;
    spawnBurst(e.clientX - rect.left, e.clientY - rect.top, 10);
  }, [spawnBurst]);

  return (
    <h1
      ref={titleRef}
      onClick={handleClick}
      className="relative cursor-pointer text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-display font-bold text-gray-900 dark:text-white mb-6 leading-[1.1] select-none"
      title="Click to react"
    >
      <HoloHeadline />
      <span className="block"><RotatingWord /></span>
      {/* Reaction bursts — pure CSS animation, removed from DOM after 1.8s */}
      <span className="pointer-events-none absolute inset-0 z-10" aria-hidden="true">
        {bursts.map((b) => (
          <span key={b.id} className="emo-burst" style={{ left: b.x, top: b.y, '--rot': `${b.rot}deg` }}>
            {b.emoji}
          </span>
        ))}
      </span>
    </h1>
  );
};

// ── Magnetic wrapper (buttons pull toward the cursor, spring back) ───────────
const Magnetic = ({ children, className = '', strength = 0.2 }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 160, damping: 14 });
  const y = useSpring(my, { stiffness: 160, damping: 14 });
  const prefersReduced = useReducedMotion();

  const handleMove = useCallback((e) => {
    if (prefersReduced) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left - rect.width / 2) * strength);
    my.set((e.clientY - rect.top - rect.height / 2) * (strength * 1.3));
  }, [mx, my, strength, prefersReduced]);

  const handleLeave = useCallback(() => { mx.set(0); my.set(0); }, [mx, my]);

  return (
    <motion.div ref={ref} onMouseMove={handleMove} onMouseLeave={handleLeave} style={{ x, y }} className={className}>
      {children}
    </motion.div>
  );
};

// ── Starfield canvas (theme-aware twinkle + drift behind the hero) ───────────
const Starfield = ({ mode }) => {
  const canvasRef = useRef(null);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let raf;
    let stars = [];
    let w = 0;
    let h = 0;

    // Pause drawing entirely while the hero is scrolled off-screen
    let visible = true;
    const io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }, { threshold: 0 });
    io.observe(canvas);

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      stars = Array.from({ length: Math.floor((w * h) / 15000) }, (_, i) => ({
        x: (i * 127.3) % w,
        y: (i * 311.7) % h,
        z: 0.3 + ((i * 73) % 100) / 143,
        tw: (((i * 41) % 100) / 100) * Math.PI * 2,
      }));
    };
    resize();
    window.addEventListener('resize', resize);

    const palette = mode === 'dark' ? ['#c9d4ff', '#c4b5fd', '#f0abfc'] : ['#94a3b8', '#a855f7', '#8b5cf6'];
    const frame = (t) => {
      if (!visible) { raf = requestAnimationFrame(frame); return; }
      ctx.clearRect(0, 0, w, h);
      stars.forEach((s, i) => {
        if (!prefersReduced) {
          s.x += s.z * 0.05;
          if (s.x > w) s.x = 0;
        }
        const twinkle = prefersReduced ? 0.6 : 0.5 + 0.5 * Math.sin(t * 0.001 * s.z * 2 + s.tw);
        ctx.globalAlpha = (mode === 'dark' ? 0.25 + twinkle * 0.5 : 0.1 + twinkle * 0.18) * s.z;
        ctx.fillStyle = palette[i % 7 === 0 ? 1 : i % 11 === 0 ? 2 : 0];
        ctx.fillRect(s.x, s.y, s.z * 1.8, s.z * 1.8);
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [mode, prefersReduced]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />;
};

// ── Hero 3D Scene (spring-tilt dashboard + depth-cued orbit + note cards) ────
const HERO_NOTE_CARDS = [
  { icon: BellIcon, label: '+1,248 new followers', sub: 'In the last hour', iconClass: 'text-green-500 bg-green-500/10 border-green-500/30', pos: { top: '2%', left: -74 }, z: 130, delay: 0 },
  { icon: RocketLaunchIcon, label: 'Published to 6 platforms', sub: 'In one click · 7s', iconClass: 'text-violet-500 bg-violet-500/10 border-violet-500/30', pos: { bottom: '9%', right: -86 }, z: 150, delay: 1.6 },
  { icon: ChatBubbleOvalLeftIcon, label: '2.4K comments today', sub: '▲ 31% vs yesterday', iconClass: 'text-pink-500 bg-pink-500/10 border-pink-500/30', pos: { top: '30%', right: -110 }, z: 95, delay: 3.1 },
];

const HeroScene = () => {
  const prefersReduced = useReducedMotion();
  const sceneRef = useRef(null);
  const visibleRef = useRef(true);
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rotateX = useSpring(tiltX, { stiffness: 60, damping: 12 });
  const rotateY = useSpring(tiltY, { stiffness: 60, damping: 12 });
  const shadowX = useTransform(rotateY, (v) => v * -2.2);
  const orbRefs = useRef([]);
  const orbitAngle = useRef(0);
  const [reach, setReach] = useState(4218904);

  useEffect(() => {
    if (prefersReduced) return undefined;
    const id = setInterval(() => {
      if (visibleRef.current) setReach((r) => r + 80 + Math.floor(Math.random() * 300));
    }, 1400);
    return () => clearInterval(id);
  }, [prefersReduced]);

  // Pause the orbit loop while the hero is off-screen
  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([entry]) => { visibleRef.current = entry.isIntersecting; }, { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useAnimationFrame((_, delta) => {
    if (prefersReduced || !visibleRef.current) return;
    orbitAngle.current += (delta / 1000) * 14;
    orbRefs.current.forEach((el, i) => {
      if (!el) return;
      const a = orbitAngle.current + (360 / PLATFORM_ICONS.length) * i;
      const near = (Math.cos((a * Math.PI) / 180) + 1) / 2; // 1 = front, 0 = behind
      el.style.transform = `rotateY(${a}deg) translateZ(225px) rotateY(${-a}deg) scale(${(0.72 + near * 0.38).toFixed(3)})`;
      el.style.opacity = (0.35 + near * 0.65).toFixed(3);
    });
  });

  const handleMouse = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    tiltY.set(((e.clientX - rect.left) / rect.width - 0.5) * 24);
    tiltX.set(-((e.clientY - rect.top) / rect.height - 0.5) * 16);
  }, [tiltX, tiltY]);
  const handleLeave = useCallback(() => { tiltX.set(0); tiltY.set(0); }, [tiltX, tiltY]);

  return (
    <div
      ref={sceneRef}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
      className="relative hidden lg:flex items-center justify-center"
      style={{ height: 560, perspective: 1400 }}
    >
      <motion.div style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }} className="relative">
        {/* 3D orbit of platform icons (depth-cued: back icons shrink, dim, blur) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ transformStyle: 'preserve-3d' }}>
          <div style={{ transformStyle: 'preserve-3d', transform: 'rotateX(12deg)' }}>
            {PLATFORM_ICONS.map((p, i) => (
              <div
                key={p.label}
                ref={(el) => { orbRefs.current[i] = el; }}
                className="absolute -top-7 -left-7 w-14 h-14 rounded-2xl bg-white/90 dark:bg-dark-800/90 backdrop-blur border border-gray-200/60 dark:border-dark-600/60 shadow-xl flex items-center justify-center"
                style={{ transformStyle: 'preserve-3d', willChange: 'transform, opacity, filter' }}
              >
                <p.icon className={`w-7 h-7 ${p.text}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Dynamic ground shadow — slides opposite the tilt */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-14 pointer-events-none">
          <motion.div style={{ x: shadowX }} className="w-72 h-10 rounded-full bg-black/30 dark:bg-black/60 blur-md" />
        </div>

        {/* Glass analytics dashboard */}
        <div
          style={{ transform: 'translateZ(60px)' }}
          className="w-[330px] rounded-3xl border border-gray-200/80 dark:border-indigo-300/20 bg-white/85 dark:bg-dark-900/85 backdrop-blur-xl shadow-2xl shadow-violet-500/10 dark:shadow-black/50 p-5 relative"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-[10px] tracking-[0.24em] text-gray-400 dark:text-gray-500">TOTAL REACH · 30D</span>
            <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-widest text-green-600 dark:text-green-400 border border-green-500/30 rounded-full px-2.5 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              LIVE
            </span>
          </div>
          <div className="font-mono text-3xl font-bold tracking-tight text-gray-900 dark:text-white tabular-nums">
            {reach.toLocaleString('en-US')}
          </div>
          <div className="font-mono text-[11px] text-green-600 dark:text-green-400 mt-1 mb-3">▲ +18.4% vs previous period</div>
          <svg viewBox="0 0 290 74" preserveAspectRatio="none" className="w-full h-[74px] text-violet-500 dark:text-cyan-400" aria-hidden="true">
            <path
              d="M0,58 C24,52 38,60 56,48 C74,36 92,44 112,38 C132,32 148,40 168,26 C188,12 208,24 232,16 C252,9 272,14 290,6 L290,74 L0,74 Z"
              fill="currentColor"
              opacity="0.15"
            />
            <path
              d="M0,58 C24,52 38,60 56,48 C74,36 92,44 112,38 C132,32 148,40 168,26 C188,12 208,24 232,16 C252,9 272,14 290,6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <circle cx="290" cy="6" r="4" fill="currentColor">
              <animate attributeName="r" values="3;5.5;3" dur="1.6s" repeatCount="indefinite" />
            </circle>
          </svg>
          <div className="grid grid-cols-3 gap-2.5 mt-3">
            {[
              { v: '312K', k: 'Likes', c: 'text-violet-600 dark:text-cyan-400' },
              { v: '48.2K', k: 'Comments', c: 'text-pink-500' },
              { v: '$24.8K', k: 'Revenue', c: 'text-amber-500' },
            ].map((chip) => (
              <div key={chip.k} className="rounded-xl border border-gray-200/70 dark:border-dark-600/60 bg-gray-50/70 dark:bg-white/[0.04] px-2.5 py-2">
                <div className={`font-mono text-sm font-bold tabular-nums ${chip.c}`}>{chip.v}</div>
                <div className="font-mono text-[8px] tracking-[0.14em] uppercase text-gray-400 dark:text-gray-500 mt-0.5">{chip.k}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating notification cards at 3D depths */}
        {HERO_NOTE_CARDS.map((n) => (
          <div key={n.label} className="absolute" style={{ ...n.pos, transform: `translateZ(${n.z}px)` }}>
            <motion.div
              animate={prefersReduced ? {} : { y: [0, -14, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: n.delay }}
              className="flex items-center gap-2.5 rounded-2xl border border-gray-200/70 dark:border-dark-600/60 bg-white/90 dark:bg-dark-800/90 backdrop-blur-xl shadow-xl px-3.5 py-2.5 whitespace-nowrap"
            >
              <span className={`w-7 h-7 rounded-lg border flex items-center justify-center ${n.iconClass}`}>
                <n.icon className="w-4 h-4" />
              </span>
              <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                {n.label}
                <span className="block font-mono text-[8.5px] font-normal tracking-[0.1em] uppercase text-gray-400 dark:text-gray-500 mt-0.5">{n.sub}</span>
              </span>
            </motion.div>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

// ── 3D Tilt Card (with cursor-tracked glare) ─────────────────────────────────
const TiltCard = ({ children, className = '' }) => {
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  const handleMouse = useCallback((e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    ref.current.style.setProperty('--gx', `${((x + 0.5) * 100).toFixed(1)}%`);
    ref.current.style.setProperty('--gy', `${((y + 0.5) * 100).toFixed(1)}%`);
    setStyle({
      transform: `perspective(800px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale3d(1.02, 1.02, 1.02)`,
      transition: 'transform 0.1s ease-out',
    });
  }, []);

  const handleLeave = useCallback(() => {
    setStyle({ transform: 'perspective(800px) rotateY(0deg) rotateX(0deg) scale3d(1, 1, 1)', transition: 'transform 0.4s ease-out' });
  }, []);

  return (
    <div ref={ref} onMouseMove={handleMouse} onMouseLeave={handleLeave} style={style} className={`tilt-parent ${className}`}>
      {children}
      <div className="tilt-glare" aria-hidden="true" />
    </div>
  );
};

// ── Scroll Progress Bar ─────────────────────────────────────────────────────
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-transparent pointer-events-none">
      <motion.div
        className="h-full bg-gradient-to-r from-orange-400 via-pink-500 to-violet-600"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// ── Live Social Proof Toast (recurring cycle) ───────────────────────────────
// Disabled until launch: the entries below are placeholder/demo data. Once the
// product is live and mature, wire this to real platform events (e.g. a
// public activity feed endpoint) and flip the flag back on.
const SHOW_SOCIAL_PROOF_TOAST = false;

const TOAST_NOTIFICATIONS = [
  { name: 'Sarah', action: 'just connected YouTube', emoji: '🎬' },
  { name: 'Marcus', action: 'grew 2.3K subscribers', emoji: '📈' },
  { name: 'Priya', action: 'just joined OmniConnX', emoji: '🚀' },
  { name: 'Alex', action: 'earned $4.2K this week', emoji: '💰' },
  { name: 'Mia', action: 'published to 6 platforms', emoji: '⚡' },
];

const LiveNotification = () => {
  const [show, setShow] = useState(false);
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * TOAST_NOTIFICATIONS.length));
  const notifications = TOAST_NOTIFICATIONS;

  useEffect(() => {
    let hideTimer;
    const showTimer = setTimeout(() => {
      setShow(true);
      hideTimer = setTimeout(() => setShow(false), 4200);
    }, 4000);
    const cycleTimer = setInterval(() => {
      setIdx((i) => (i + 1) % TOAST_NOTIFICATIONS.length);
      setShow(true);
      hideTimer = setTimeout(() => setShow(false), 4200);
    }, 10000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearInterval(cycleTimer);
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed bottom-6 left-6 z-50 flex items-center gap-3 bg-white/90 dark:bg-dark-800/90 backdrop-blur-xl border border-gray-200/50 dark:border-dark-600/50 rounded-2xl px-5 py-3.5 shadow-2xl max-w-xs"
        >
          <span className="text-xl flex-shrink-0">{notifications[idx].emoji}</span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              <span className="font-semibold">{notifications[idx].name}</span> {notifications[idx].action}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">just now</p>
          </div>
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ── Neural Hub — canvas pulse engine (ported from design preview) ────────────
const HUB_LOGO_PATHS = {
  YouTube: 'M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4L15.8 12l-6.2 3.6z',
  TikTok: 'M19.6 5.8a5 5 0 0 1-3.5-4.3V1h-3.4v13.7a2.9 2.9 0 1 1-2.9-2.9c.3 0 .6 0 .9.1V8.4a6.3 6.3 0 1 0 5.4 6.2V8.9a8.3 8.3 0 0 0 4.8 1.5V7a5 5 0 0 1-1.3-1.2z',
  Instagram: 'M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.2 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.2 1.8-.4 2.2a3.8 3.8 0 0 1-.9 1.4 3.8 3.8 0 0 1-1.4.9c-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.2-2.2-.4a3.8 3.8 0 0 1-1.4-.9 3.8 3.8 0 0 1-.9-1.4c-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.2-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 3.7a6.1 6.1 0 1 0 0 12.2 6.1 6.1 0 0 0 0-12.2zm0 10a4 4 0 1 1 0-7.9 4 4 0 0 1 0 7.9zm6.4-11.8a1.4 1.4 0 1 0 0 2.9 1.4 1.4 0 0 0 0-2.9z',
  Facebook: 'M24 12a12 12 0 1 0-13.9 11.9v-8.4h-3V12h3V9.4c0-3 1.8-4.7 4.6-4.7 1.3 0 2.7.2 2.7.2v3h-1.5c-1.5 0-2 .9-2 1.9V12h3.3l-.5 3.5h-2.8v8.4A12 12 0 0 0 24 12z',
  Twitch: 'M4.3 1 1.5 4.8v15.4h5.2V23h2.9l2.8-2.8h4.3l5.8-5.7V1H4.3zm16.2 12.5-3.3 3.3h-5.2l-2.8 2.8v-2.8H4.9V3h15.6v10.5zM17 6.7h-2v5.8h2V6.7zm-5.3 0h-2v5.8h2V6.7z',
  'X (Twitter)': 'M18.9 1.2h3.7l-8.1 9.3L24 22.8h-7.5l-5.9-7.7-6.7 7.7H.2l8.7-9.9L0 1.2h7.7l5.3 7 6-7z',
};
const HUB_COLORS_DARK = { YouTube: '#ff4444', TikTok: '#69e5f0', Instagram: '#ff5e8a', Facebook: '#4d94ff', Twitch: '#a970ff', 'X (Twitter)': '#cdd6f4' };
const HUB_COLORS_LIGHT = { YouTube: '#e53935', TikTok: '#0891b2', Instagram: '#db2777', Facebook: '#1877f2', Twitch: '#7c3aed', 'X (Twitter)': '#1e293b' };

const NeuralHubCanvas = ({ mode }) => {
  const canvasRef = useRef(null);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const dark = mode === 'dark';

    // Pause the whole engine while the hub section is off-screen
    let visible = true;
    const io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }, { threshold: 0 });
    io.observe(canvas);
    const COLORS = dark ? HUB_COLORS_DARK : HUB_COLORS_LIGHT;
    const PLAT = Object.keys(HUB_LOGO_PATHS).map((key) => ({
      key,
      color: COLORS[key],
      path: new Path2D(HUB_LOGO_PATHS[key]),
    }));

    let raf;
    let W = 0;
    let H = 0;
    let packets = [];
    let lastWave = 0;
    let waveT = -9999;
    const nodeFlash = PLAT.map(() => -9999);

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const nodePos = (i, t) => {
      const cx = W / 2;
      const cy = H / 2;
      const rx = Math.min(W * 0.38, 420);
      const ry = Math.min(H * 0.36, 190);
      const a = ((Math.PI * 2) / PLAT.length) * i + (prefersReduced ? 0 : t * 0.00012);
      return { x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry };
    };

    const draw = (t) => {
      if (!visible) {
        if (!prefersReduced) raf = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, W, H);
      const cx = W / 2;
      const cy = H / 2;

      // Dashed orbital guide
      ctx.save();
      ctx.setLineDash([3, 9]);
      ctx.strokeStyle = dark ? 'rgba(140,150,255,0.14)' : 'rgba(100,116,139,0.28)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(cx, cy, Math.min(W * 0.38, 420), Math.min(H * 0.36, 190), 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      // Curved links with per-platform gradient
      PLAT.forEach((p, i) => {
        const n = nodePos(i, t);
        const g = ctx.createLinearGradient(n.x, n.y, cx, cy);
        g.addColorStop(0, `${p.color}55`);
        g.addColorStop(1, dark ? 'rgba(78,225,255,0.13)' : 'rgba(168,85,247,0.18)');
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(n.x, n.y);
        ctx.quadraticCurveTo((n.x + cx) / 2, (n.y + cy) / 2 - 34, cx, cy);
        ctx.stroke();
      });

      // Synchronized wave: core emits one pulse to every platform at once
      if (!prefersReduced && t - lastWave > 1600) {
        for (let w = 0; w < PLAT.length; w += 1) packets.push({ i: w, p: 0 });
        lastWave = t;
        waveT = t;
      }
      packets.forEach((pk) => {
        pk.p += 0.013;
        const prog = Math.min(pk.p, 1);
        const n = nodePos(pk.i, t);
        const tt = 1 - prog;
        const mx = (n.x + cx) / 2;
        const my = (n.y + cy) / 2 - 34;
        const bez = (k) => ({
          x: (1 - k) * (1 - k) * n.x + 2 * (1 - k) * k * mx + k * k * cx,
          y: (1 - k) * (1 - k) * n.y + 2 * (1 - k) * k * my + k * k * cy,
        });
        const head = bez(tt);
        const tail = bez(1 - Math.max(prog - 0.09, 0));
        const grad = ctx.createLinearGradient(tail.x, tail.y, head.x, head.y);
        grad.addColorStop(0, `${PLAT[pk.i].color}00`);
        grad.addColorStop(1, PLAT[pk.i].color);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(tail.x, tail.y);
        ctx.lineTo(head.x, head.y);
        ctx.stroke();
        ctx.beginPath();
        ctx.fillStyle = PLAT[pk.i].color;
        ctx.arc(head.x, head.y, 3.2, 0, Math.PI * 2);
        ctx.fill();
      });
      packets.forEach((pk) => { if (pk.p >= 1) nodeFlash[pk.i] = t; });
      packets = packets.filter((pk) => pk.p < 1);

      // Core — breathing halo plus a burst on each wave emit
      const burst = Math.max(0, 1 - (t - waveT) / 500) * 10;
      const pulse = (prefersReduced ? 0 : Math.sin(t * 0.003) * 5) + burst;
      const coreGrad = ctx.createRadialGradient(cx, cy, 4, cx, cy, 60 + pulse);
      if (dark) {
        coreGrad.addColorStop(0, 'rgba(78,225,255,0.95)');
        coreGrad.addColorStop(0.35, 'rgba(139,92,246,0.5)');
        coreGrad.addColorStop(1, 'rgba(139,92,246,0)');
      } else {
        coreGrad.addColorStop(0, 'rgba(168,85,247,0.6)');
        coreGrad.addColorStop(0.4, 'rgba(139,92,246,0.28)');
        coreGrad.addColorStop(1, 'rgba(139,92,246,0)');
      }
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, 60 + pulse, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = dark ? '#07070f' : '#ffffff';
      ctx.beginPath();
      ctx.arc(cx, cy, 28, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = dark ? 'rgba(78,225,255,0.85)' : 'rgba(168,85,247,0.85)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, 28, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = dark ? '#4ee1ff' : '#a855f7';
      ctx.font = '700 10px "Roboto Mono", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('OMNI', cx, cy - 6);
      ctx.fillText('CORE', cx, cy + 6);

      // Platform nodes with real brand logos, breathing + arrival flash/ripple
      PLAT.forEach((p, i) => {
        const n = nodePos(i, t);
        const flashDecay = Math.max(0, 1 - (t - nodeFlash[i]) / 650);
        const breathe = prefersReduced ? 0 : Math.sin(t * 0.002 + i * 1.1) * 2;
        const rad = 26 + breathe + flashDecay * 3;
        if (flashDecay > 0) {
          ctx.beginPath();
          ctx.strokeStyle = p.color;
          ctx.globalAlpha = flashDecay * 0.7;
          ctx.lineWidth = 1.6;
          ctx.arc(n.x, n.y, rad + (1 - flashDecay) * 26, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
        // soft glow via radial gradient (shadowBlur is a per-frame perf killer)
        const glowR = rad * (2.0 + flashDecay * 0.6);
        const glow = ctx.createRadialGradient(n.x, n.y, rad * 0.4, n.x, n.y, glowR);
        glow.addColorStop(0, `${p.color}${dark ? '2e' : '1f'}`);
        glow.addColorStop(1, `${p.color}00`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(n.x, n.y, glowR, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.fillStyle = dark ? 'rgba(12,12,28,0.95)' : '#ffffff';
        ctx.arc(n.x, n.y, rad, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 1.6 + flashDecay * 1.2;
        ctx.stroke();
        const s = (rad * 1.05) / 24;
        ctx.save();
        ctx.translate(n.x - 12 * s, n.y - 12 * s);
        ctx.scale(s, s);
        ctx.fillStyle = p.color;
        ctx.fill(p.path);
        ctx.restore();
      });

      if (!prefersReduced) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [mode, prefersReduced]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

// ── Neural Hub — spring 3D tilt wrapper + floating status badges ─────────────
const NeuralHub = ({ mode }) => {
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rotateX = useSpring(tiltX, { stiffness: 50, damping: 14 });
  const rotateY = useSpring(tiltY, { stiffness: 50, damping: 14 });
  const prefersReduced = useReducedMotion();

  const handleMouse = useCallback((e) => {
    if (prefersReduced) return;
    const rect = e.currentTarget.getBoundingClientRect();
    tiltY.set(((e.clientX - rect.left) / rect.width - 0.5) * 16);
    tiltX.set(-((e.clientY - rect.top) / rect.height - 0.5) * 12);
  }, [tiltX, tiltY, prefersReduced]);
  const handleLeave = useCallback(() => { tiltX.set(0); tiltY.set(0); }, [tiltX, tiltY]);

  return (
    <div className="hidden lg:block" style={{ perspective: 1200 }} onMouseMove={handleMouse} onMouseLeave={handleLeave}>
      <motion.div
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        className="relative mx-auto max-w-5xl h-[520px]"
      >
        <NeuralHubCanvas mode={mode} />

        {/* Floating status badges — lifted on the z-axis so the tilt reads as 3D */}
        <motion.div
          className="absolute top-4 right-8 flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/80 dark:bg-dark-800/80 backdrop-blur-md border border-gray-200/50 dark:border-dark-600/50 shadow-lg"
          style={{ transform: 'translateZ(50px)' }}
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1.2 }}
        >
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">6 Platforms Synced</span>
        </motion.div>
        <motion.div
          className="absolute bottom-6 left-8 flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/80 dark:bg-dark-800/80 backdrop-blur-md border border-gray-200/50 dark:border-dark-600/50 shadow-lg"
          style={{ transform: 'translateZ(50px)' }}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1.5 }}
        >
          <SparklesIcon className="w-3.5 h-3.5 text-violet-500" />
          <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">AI Processing Active</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

// ── 3D Mini-Phone Creator Card (float + tilt + springy flip) ─────────────────
const PhoneSideButtons = ({ mirrored }) => (
  <>
    <span className={`absolute ${mirrored ? '-left-[2px]' : '-right-[2px]'} top-[150px] w-[3px] h-14 rounded bg-gray-400 dark:bg-[#4a5068]`} />
    <span className={`absolute ${mirrored ? '-right-[2px]' : '-left-[2px]'} top-[120px] w-[3px] h-10 rounded bg-gray-400 dark:bg-[#4a5068]`} />
    <span className={`absolute ${mirrored ? '-right-[2px]' : '-left-[2px]'} top-[166px] w-[3px] h-10 rounded bg-gray-400 dark:bg-[#4a5068]`} />
  </>
);

const PhoneScreenChrome = ({ battery }) => (
  <>
    <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-[74px] h-[20px] bg-black rounded-[11px] z-10">
      <span className="absolute right-[7px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gradient-to-br from-slate-700 to-black" />
    </div>
    <div className="flex justify-between items-center font-mono text-[10px] font-semibold text-gray-700 dark:text-gray-300 px-1 mb-1">
      <span>9:41</span>
      <span>5G ▮ {battery}%</span>
    </div>
  </>
);

const CreatorPhoneCard = ({ creator, index }) => {
  const [flipped, setFlipped] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const hoveredRef = useRef(false);
  hoveredRef.current = hovered;
  const prefersReduced = useReducedMotion();

  // Auto-flip timer — staggered start, pauses on hover
  useEffect(() => {
    let intervalId;
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        if (!hoveredRef.current) setFlipped((f) => !f);
      }, 5000);
    }, 2500 + index * 1600);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [index]);

  const handleMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ ry: px * 14, rx: -py * 11 });
  }, []);

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 60, scale: 0.85 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.2, duration: 0.8, type: 'spring', stiffness: 100 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setTilt({ rx: 0, ry: 0 }); }}
      onMouseMove={handleMove}
    >
      {/* Breathing ground shadow */}
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 -bottom-7 w-40 h-5 rounded-full bg-black/40 blur-md pointer-events-none"
        animate={prefersReduced ? {} : { scale: [1, 0.85, 1], opacity: [0.5, 0.32, 0.5] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: index * 1.2 }}
      />

      {/* Levitation */}
      <motion.div
        animate={prefersReduced ? {} : { y: [0, -12, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: index * 1.2 }}
      >
        {/* Cursor tilt */}
        <div style={{ perspective: 1100 }}>
          <div
            style={{
              transform: `rotateY(${tilt.ry.toFixed(2)}deg) rotateX(${tilt.rx.toFixed(2)}deg)`,
              transition: 'transform 0.3s ease-out',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Springy flip */}
            <div
              className="relative cursor-pointer"
              style={{
                transformStyle: 'preserve-3d',
                transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                transition: 'transform 0.95s cubic-bezier(0.3, 1.35, 0.45, 1)',
              }}
              onClick={() => setFlipped((f) => !f)}
              onKeyDown={(e) => e.key === 'Enter' && setFlipped((f) => !f)}
              tabIndex={0}
              role="button"
              aria-label={`${creator.name} ${creator.platform} profile phone — tap to flip to analytics`}
            >
              {/* ── FRONT: profile app screen ── */}
              <div
                className="w-[250px] rounded-[44px] p-[9px] relative bg-gradient-to-br from-gray-300 via-gray-100 to-gray-400 dark:from-[#3d4358] dark:via-[#171923] dark:to-[#40465c] shadow-2xl shadow-black/30"
                style={{ backfaceVisibility: 'hidden' }}
              >
                <PhoneSideButtons mirrored={false} />
                <div className="rounded-[36px] overflow-hidden bg-white dark:bg-[#0b0d19] relative flex flex-col px-4 pt-3 pb-3.5 h-[488px]">
                  <PhoneScreenChrome battery={creator.battery} />

                  {/* Story-ring avatar */}
                  <div className={`w-[72px] h-[72px] rounded-full p-[2.5px] mx-auto mt-4 mb-2.5 bg-gradient-to-tr ${creator.gradient}`}>
                    <div className={`w-full h-full rounded-full bg-gradient-to-br ${creator.gradient} border-[2.5px] border-white dark:border-[#0b0d19] flex items-center justify-center text-white font-bold text-lg`}>
                      {creator.avatar}
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="font-display font-bold text-[15px] text-gray-900 dark:text-white">{creator.name}</span>
                    <span className="text-[11px] text-violet-500 dark:text-cyan-400">✔</span>
                  </div>
                  <p className="text-center font-mono text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 flex items-center justify-center gap-1">
                    {creator.handle} · <creator.platformIcon className="w-3 h-3 inline" /> {creator.platform}
                  </p>
                  <p className="text-center text-[11px] text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">{creator.bio}</p>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 mt-3.5 py-2.5 border-y border-gray-100 dark:border-dark-700">
                    {creator.stats.map((s) => (
                      <div key={s.label} className="text-center">
                        <p className="font-mono text-[13px] font-bold text-gray-900 dark:text-white tabular-nums">{s.value}</p>
                        <p className="font-mono text-[7.5px] uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className={`mt-3 py-2.5 rounded-xl bg-gradient-to-r ${creator.gradient} text-white text-xs font-bold text-center shadow-lg`}>
                    {creator.ctaLabel}
                  </div>

                  {/* Recent posts tiles */}
                  <div className="grid grid-cols-3 gap-1.5 mt-3">
                    {[0.9, 0.7, 0.8].map((op, i) => (
                      <div key={i} className={`h-[52px] rounded-lg bg-gradient-to-br ${creator.gradient} relative`} style={{ opacity: op }}>
                        <span className="absolute inset-0 flex items-center justify-center text-white/75 text-[10px]">▶</span>
                      </div>
                    ))}
                  </div>

                  {/* Bottom tab bar + hint */}
                  <div className="mt-auto pt-2.5 border-t border-gray-100 dark:border-dark-700 flex justify-around text-[13px] text-gray-400 dark:text-gray-500">
                    <span className="text-gray-900 dark:text-white">⌂</span><span>▷</span><span>＋</span><span>♡</span><span>◎</span>
                  </div>
                  <p className="text-center font-mono text-[8px] tracking-[0.2em] uppercase text-gray-300 dark:text-gray-600 mt-2">◈ Tap for analytics</p>
                </div>
                {/* Screen glass highlight */}
                <div className="absolute inset-[9px] rounded-[36px] pointer-events-none bg-gradient-to-br from-white/10 via-transparent to-transparent" />
              </div>

              {/* ── BACK: analytics app screen ── */}
              <div
                className="absolute inset-0 w-[250px] rounded-[44px] p-[9px] bg-gradient-to-br from-gray-300 via-gray-100 to-gray-400 dark:from-[#3d4358] dark:via-[#171923] dark:to-[#40465c] shadow-2xl shadow-black/30"
                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
              >
                <PhoneSideButtons mirrored />
                <div className="rounded-[36px] overflow-hidden bg-white dark:bg-[#0b0d19] relative flex flex-col px-4 pt-3 pb-3.5 h-[488px]">
                  <PhoneScreenChrome battery={creator.battery} />

                  <div className="flex items-center justify-between mt-4">
                    <div>
                      <p className="font-display font-bold text-[15px] text-gray-900 dark:text-white">Analytics</p>
                      <p className="font-mono text-[8.5px] tracking-[0.14em] uppercase text-gray-400 dark:text-gray-500 mt-0.5">30-day window</p>
                    </div>
                    <span className="inline-flex items-center gap-1 font-mono text-[8.5px] tracking-widest text-green-600 dark:text-green-400 border border-green-500/30 rounded-full px-2 py-0.5">
                      <span className="w-1 h-1 rounded-full bg-green-500 animate-pulse" />
                      LIVE
                    </span>
                  </div>

                  {/* 2×2 metric cells */}
                  <div className="grid grid-cols-2 gap-2 mt-3.5">
                    {creator.backStats.map((s) => (
                      <div key={s.label} className="rounded-xl border border-gray-100 dark:border-dark-700 bg-gray-50/80 dark:bg-white/[0.04] px-2.5 py-2">
                        <p className="font-mono text-[13px] font-bold text-gray-900 dark:text-white tabular-nums">{s.value}</p>
                        <p className="font-mono text-[8.5px] text-green-500 mt-0.5">▲ {s.trend}</p>
                        <p className="font-mono text-[7px] uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500 mt-1">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Sparkline */}
                  <div className="mt-3.5 p-3 rounded-xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-100 dark:border-dark-700">
                    <p className="font-mono text-[7.5px] uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 mb-1.5">Growth trend</p>
                    <svg viewBox="0 0 220 56" preserveAspectRatio="none" className="w-full h-12" style={{ color: creator.accent }} aria-hidden="true">
                      <path d="M0,46 C22,42 36,46 54,38 C72,30 88,34 106,26 C124,18 140,22 158,13 C176,6 198,11 220,4 L220,56 L0,56 Z" fill="currentColor" opacity="0.14" />
                      <path d="M0,46 C22,42 36,46 54,38 C72,30 88,34 106,26 C124,18 140,22 158,13 C176,6 198,11 220,4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>

                  <p className="mt-auto text-center font-mono text-[8px] tracking-[0.2em] uppercase text-gray-300 dark:text-gray-600 pt-2">◈ Tap to flip back</p>
                </div>
                <div className="absolute inset-[9px] rounded-[36px] pointer-events-none bg-gradient-to-br from-white/10 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// LANDING PAGE
// ═════════════════════════════════════════════════════════════════════════════
const BURST_EMOJI = ['❤️', '🔥', '👍', '💬', '⭐', '🎉', '👀', '💜'];

const LandingPage = () => {
  const dispatch = useDispatch();
  const { mode } = useSelector((state) => state.theme);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Hero spotlight — motion values so mousemove never re-renders the page tree
  const heroRef = useRef(null);
  const spotX = useMotionValue(50);
  const spotY = useMotionValue(50);
  const spotlight = useMotionTemplate`radial-gradient(800px circle at ${spotX}% ${spotY}%, rgba(168,85,247,0.07), transparent 40%)`;
  const handleHeroMouse = useCallback((e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    spotX.set(((e.clientX - rect.left) / rect.width) * 100);
    spotY.set(((e.clientY - rect.top) / rect.height) * 100);
  }, [spotX, spotY]);

  // Demo video lightbox
  const [demoOpen, setDemoOpen] = useState(false);
  useEffect(() => {
    if (!demoOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDemoOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [demoOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-violet-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-800 overflow-x-hidden">

      {/* ── Scroll Progress Bar ──────────────────────────────────────── */}
      <ScrollProgress />

      {/* ── Live Social Proof Toast ──────────────────────────────────── */}
      {SHOW_SOCIAL_PROOF_TOAST && <LiveNotification />}

      {/* ── Background Blobs (ambient) ─────────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-fuchsia-400/10 dark:bg-fuchsia-400/5 blob animate-blob rounded-full blur-3xl" />
        <div className="absolute bottom-[-15%] left-[-10%] w-[600px] h-[600px] bg-purple-400/10 dark:bg-purple-400/5 blob-2 animate-blob rounded-full blur-3xl" style={{ animationDelay: '2s' }} />
        <div className="absolute top-[40%] left-[50%] w-[400px] h-[400px] bg-pink-400/8 dark:bg-pink-400/3 blob animate-blob rounded-full blur-3xl" style={{ animationDelay: '4s' }} />
      </div>

      {/* ── Navigation ──────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 dark:bg-dark-900/70 backdrop-blur-xl border-b border-gray-200/50 dark:border-dark-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center">
              <BrandLogo size="md" />
            </motion.div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-6">
                {['Pricing', 'How It Works', 'Help', 'About', 'Contact'].map((item) => (
                  <Link key={item} to={`/${item.toLowerCase().replace(/ /g, '-')}`} className="nav-underline text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                    {item}
                  </Link>
                ))}
              </div>

              <button onClick={() => dispatch(toggleTheme())} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors" aria-label="Toggle theme">
                {mode === 'light' ? <MoonIcon className="w-5 h-5 text-gray-600" /> : <SunIcon className="w-5 h-5 text-gray-400" />}
              </button>

              {isAuthenticated ? (
                <Link to="/dashboard" className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md text-sm">Dashboard</Link>
              ) : (
                <div className="hidden md:flex items-center space-x-2">
                  <Link to="/login" className="btn btn-ghost text-sm">Login</Link>
                  <Link to="/register" className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md text-sm">Get Started</Link>
                </div>
              )}

              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800">
                {mobileMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="md:hidden pb-4 pt-2 space-y-2">
              {['Pricing', 'How It Works', 'Help', 'About', 'Contact'].map((item) => (
                <Link key={item} to={`/${item.toLowerCase().replace(/ /g, '-')}`} className="block px-2 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-dark-800">
                  {item}
                </Link>
              ))}
              {!isAuthenticated && (
                <div className="flex gap-2 pt-2">
                  <Link to="/login" className="btn btn-ghost flex-1 text-center text-sm">Login</Link>
                  <Link to="/register" className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md flex-1 text-center text-sm">Get Started</Link>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </nav>

      {/* ── Hero — Holo Headline + 3D Command Deck Scene ───────────────── */}
      <section ref={heroRef} onMouseMove={handleHeroMouse} className="pt-28 sm:pt-36 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Starfield + perspective grid floor */}
        <Starfield mode={mode} />
        <div className="hero-grid-floor hidden sm:block" aria-hidden="true" />
        {/* Mouse-following gradient spotlight (motion-value driven, zero re-renders) */}
        <motion.div
          className="absolute inset-0 pointer-events-none z-0 hidden lg:block"
          style={{ background: spotlight }}
        />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-8 items-center relative z-[1]">
          {/* Left — Text */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 text-sm font-medium mb-8 border border-violet-200/50 dark:border-violet-700/30"
            >
              <RocketLaunchIcon className="w-4 h-4" />
              AI-Powered Creator Management
            </motion.div>

            <HeroHeadline />

            <p className="text-lg sm:text-xl text-gray-500 dark:text-gray-400 mb-10 max-w-xl leading-relaxed">
              Manage{' '}
              <span className="inline-flex items-center gap-1 font-semibold text-red-500"><FaYoutube className="w-4 h-4" /> YouTube</span>,{' '}
              <span className="inline-flex items-center gap-1 font-semibold text-cyan-500 dark:text-cyan-400"><FaTiktok className="w-4 h-4" /> TikTok</span>,{' '}
              <span className="inline-flex items-center gap-1 font-semibold text-pink-500"><FaInstagram className="w-4 h-4" /> Instagram</span>{' '}
              & more from one intelligent dashboard.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <Magnetic>
                <Link
                  to="/register"
                  className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white block text-lg px-8 py-4 shadow-2xl shadow-violet-600/30 hover:shadow-violet-600/50 transition-all text-center glow-hover"
                >
                  Start Free Trial
                </Link>
              </Magnetic>
              <Magnetic>
                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  className="btn btn-secondary block w-full text-lg px-8 py-4 transition-all text-center"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <PlayIcon className="w-5 h-5" />
                    Watch Demo
                  </span>
                </button>
              </Magnetic>
            </div>

            {/* Microcopy + trust signals */}
            <p className="font-mono text-[11px] tracking-wide text-gray-400 dark:text-gray-500 mb-6">
              <span className="text-green-500 font-bold">✓</span> No credit card required
              <span className="mx-2">·</span>
              <span className="text-green-500 font-bold">✓</span> Free forever plan
            </p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex items-center gap-4 mb-10"
            >
              <div className="flex">
                {[
                  'from-red-500 to-rose-600',
                  'from-blue-500 to-violet-500',
                  'from-fuchsia-400 to-purple-500',
                  'from-amber-400 to-orange-500',
                ].map((g, i) => (
                  <span
                    key={g}
                    className={`w-9 h-9 rounded-full bg-gradient-to-br ${g} border-2 border-white dark:border-dark-950 flex items-center justify-center text-[11px] font-bold text-white ${i > 0 ? '-ml-2.5' : ''}`}
                  >
                    {['SC', 'MR', 'PS', 'JT'][i]}
                  </span>
                ))}
                <span className="w-9 h-9 rounded-full bg-gray-100 dark:bg-dark-800 border-2 border-white dark:border-dark-950 -ml-2.5 flex items-center justify-center font-mono text-[9px] font-bold text-gray-500 dark:text-gray-400">
                  +500
                </span>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => <StarSolid key={i} className="w-3.5 h-3.5 text-amber-400" />)}
                  </span>
                  <span className="font-mono text-sm font-bold text-gray-900 dark:text-white">4.9</span>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">from 180+ creator reviews</p>
              </div>
            </motion.div>

            {/* Inline stats with animated dividers */}
            <div className="flex flex-wrap gap-8 text-sm">
              {[
                { value: '500+', label: 'Creators', color: 'text-fuchsia-500' },
                { value: '99.9%', label: 'Uptime', color: 'text-green-500' },
                { value: '4', label: 'AI Models', color: 'text-purple-500' },
              ].map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 + i * 0.15 }}
                  className="relative"
                >
                  <span className={`text-2xl font-bold ${s.color}`}>{s.value}</span>
                  <p className="text-gray-400 dark:text-gray-500">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right — 3D Command Deck Scene (spring tilt + depth-cued orbit) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, type: 'spring', stiffness: 80 }}
          >
            <HeroScene />
          </motion.div>

          {/* Mobile platform icons */}
          <div className="lg:hidden flex justify-center gap-3 flex-wrap">
            {PLATFORM_ICONS.map((p) => (
              <motion.div
                key={p.label}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.95 }}
                className={`w-12 h-12 rounded-xl ${p.bg} flex items-center justify-center shadow-sm`}
              >
                <p.icon className={`w-6 h-6 ${p.text}`} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social Proof Marquee (dual counter-scrolling, edge-faded) ──── */}
      <section className="py-4 bg-white/40 dark:bg-dark-900/40 border-y border-gray-200/30 dark:border-dark-700/30 hover-pause marquee-fade">
        <div className="overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span key={i} className="flex items-center mx-8 text-sm font-medium text-gray-400 dark:text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500 mr-4 flex-shrink-0" />
                {item}
              </span>
            ))}
          </div>
        </div>
        <div className="overflow-hidden mt-3">
          <div className="flex animate-marquee-reverse whitespace-nowrap">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span key={i} className="flex items-center mx-8 text-sm font-medium text-gray-400 dark:text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-pink-500/70 mr-4 flex-shrink-0" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trusted By Logos ── Hidden for now (placeholder brands, not real customers).
           Flip this back to `true` to restore. ── */}
      {false && (
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 font-medium mb-6">Trusted by creators and teams at</p>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4">
            {['Streamlabs', 'Patreon', 'Shopify', 'Canva', 'Notion'].map((brand, i) => (
              <motion.span
                key={brand}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-lg font-display font-semibold text-gray-300 dark:text-dark-600 hover:text-gray-400 dark:hover:text-dark-500 transition-colors cursor-default"
              >
                {brand}
              </motion.span>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── Bento Grid Features (with 3D tilt + animated illustrations) ── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
              Features That <span className="shimmer-text">Set You Apart</span>
            </h2>
            <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
              Everything you need to manage and grow your creator business, powered by AI
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5">
            {/* 1 — Real-time analytics radar (wide) */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-4">
              <TiltCard className="card p-8 h-full group hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
                    <ChartBarIcon className="w-7 h-7 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Real-time analytics radar</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Cross-platform views, watch-time and engagement streamed onto one dashboard. Anomalies glow before they become problems.</p>
                  </div>
                </div>
                <div className="flex items-end gap-2 h-28 mt-2 px-2">
                  {[35, 55, 40, 70, 50, 85, 60, 90, 55, 95, 65, 80].map((h, i) => (
                    <motion.div
                      key={i}
                      className="flex-1 rounded-t-md bg-gradient-to-t from-violet-600 to-violet-300 opacity-60 group-hover:opacity-100 transition-opacity duration-500 bar-pulse"
                      style={{ animationDelay: `${i * 0.15}s` }}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.06, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                    />
                  ))}
                </div>
              </TiltCard>
            </motion.div>

            {/* 2 — One-click publish (equalizer) */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="lg:col-span-2">
              <TiltCard className="card p-8 h-full group hover:shadow-2xl transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
                  <BoltIcon className="w-7 h-7 text-pink-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">One-click publish</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Push a video to all six platforms with per-platform captions, thumbnails and settings.</p>
                <div className="flex items-center gap-1 h-10 mt-6" aria-hidden="true">
                  {[...Array(14)].map((_, i) => (
                    <span
                      key={i}
                      className="w-1 h-full rounded-full bg-pink-500/80 eq-bar"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>
              </TiltCard>
            </motion.div>

            {/* 3 — AI co-pilot */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-2">
              <TiltCard className="card p-8 h-full group hover:shadow-2xl transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
                  <SparklesIcon className="w-7 h-7 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">AI co-pilot</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Captions, hashtags, titles and content ideas generated from what actually performs on your channels.</p>
                <div className="space-y-2">
                  {['Caption drafted for 3 platforms', 'Trending hashtags refreshed'].map((line, i) => (
                    <motion.div
                      key={line}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6 + i * 0.3 }}
                      className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
                    >
                      <SparklesIcon className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                      {line}
                    </motion.div>
                  ))}
                </div>
              </TiltCard>
            </motion.div>

            {/* 4 — Revenue radar */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="lg:col-span-2">
              <TiltCard className="card p-8 h-full group hover:shadow-2xl transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
                  <CurrencyDollarIcon className="w-7 h-7 text-yellow-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Revenue radar</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">AdSense, brand deals and creator funds reconciled automatically. Discrepancies flagged in real time.</p>
                <div className="text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
                  $<AnimatedCounter target={12847} />
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-500 font-semibold">+23.5%</span>
                  <span className="text-xs text-gray-400">this month</span>
                </div>
              </TiltCard>
            </motion.div>

            {/* 5 — Health monitoring */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="lg:col-span-2">
              <TiltCard className="card p-8 h-full group hover:shadow-2xl transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
                  <HeartIcon className="w-7 h-7 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Health monitoring</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Anomaly detection watches every channel&rsquo;s vitals and pings you before the algorithm does.</p>
                <svg viewBox="0 0 200 60" className="w-full" preserveAspectRatio="none" aria-hidden="true">
                  <motion.polyline
                    points="0,30 25,30 34,10 43,50 52,18 61,42 70,30 100,30 109,10 118,50 127,18 136,42 145,30 175,30 184,14 193,46 200,30"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="text-green-500"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2.5, ease: 'easeInOut' }}
                  />
                </svg>
                <div className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/15 border border-green-200/50 dark:border-green-800/30">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                    <span className="text-sm font-bold text-white">92</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Health Score</p>
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">Excellent — all channels healthy</p>
                  </div>
                </div>
              </TiltCard>
            </motion.div>

            {/* 6 — Built-in video editor */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-3">
              <TiltCard className="card p-8 h-full group hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
                    <FilmIcon className="w-7 h-7 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Built-in video editor</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Trim, caption, filter and convert aspect ratios for every platform — right in the browser, no downloads.</p>
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-2" aria-hidden="true">
                  <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-dark-800 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-pink-500 to-violet-600"
                      initial={{ width: '0%' }}
                      whileInView={{ width: '72%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, delay: 0.4, ease: 'easeOut' }}
                    />
                  </div>
                  <span className="font-mono text-[10px] text-gray-400 dark:text-gray-500">00:42 / 00:58</span>
                </div>
              </TiltCard>
            </motion.div>

            {/* 7 — Brand-deal pipeline */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="lg:col-span-3">
              <TiltCard className="card p-8 h-full group hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
                    <BriefcaseIcon className="w-7 h-7 text-pink-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Brand-deal pipeline</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Deals, deliverables and AI-generated rate cards in a pipeline that runs itself — deadline reminders included.</p>
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-2" aria-hidden="true">
                  {['Lead', 'Negotiation', 'Contract', 'Delivered'].map((stage, i) => (
                    <motion.span
                      key={stage}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.15, type: 'spring', stiffness: 200 }}
                      className={`font-mono text-[10px] tracking-wide px-2.5 py-1 rounded-full border ${i === 3 ? 'border-green-500/40 text-green-600 dark:text-green-400 bg-green-500/10' : 'border-gray-200 dark:border-dark-600 text-gray-400 dark:text-gray-500'}`}
                    >
                      {stage}{i < 3 && ' →'}
                    </motion.span>
                  ))}
                </div>
              </TiltCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Platform Hub — Interactive Neural Network ─────────────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/30 dark:bg-dark-900/30 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[500px] h-[500px] rounded-full bg-violet-500/5 dark:bg-violet-500/[0.03] blur-[100px]" />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
              All Platforms. <span className="shimmer-text">One Pulse.</span>
            </h2>
            <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
              Connect all your creator accounts to a single intelligent dashboard
            </p>
          </motion.div>

          {/* Hub — Desktop: canvas pulse engine with spring 3D tilt */}
          <NeuralHub mode={mode} />

          {/* Hub — Mobile */}
          <div className="lg:hidden">
            <div className="flex justify-center mb-6">
              <img src={brandAsset.symbol(128)} alt="OmniConnX" className="w-16 h-16 drop-shadow-xl" />
            </div>
            <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto">
              {PLATFORM_ICONS.map((p, i) => (
                <motion.div key={p.label} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex flex-col items-center gap-2">
                  <div className={`w-12 h-12 rounded-xl ${p.bg} flex items-center justify-center`}><p.icon className={`w-6 h-6 ${p.text}`} /></div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{p.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
              Get Started in <span className="shimmer-text">3 Simple Steps</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-20 left-[18%] right-[18%] h-[2px] overflow-hidden">
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: 'easeInOut' }}
                className="h-full bg-gradient-to-r from-blue-400 via-violet-400 to-orange-400 origin-left"
              />
            </div>

            {[
              { step: 1, icon: LinkIcon, title: 'Connect Platforms', desc: 'Link YouTube, TikTok, Instagram & more in seconds with secure OAuth.', color: 'from-blue-500 to-cyan-500', glow: 'shadow-blue-500/30' },
              { step: 2, icon: SparklesIcon, title: 'AI Analyzes', desc: 'Our ML models detect trends, anomalies, and growth opportunities in real-time.', color: 'from-fuchsia-500 to-violet-500', glow: 'shadow-violet-500/30' },
              { step: 3, icon: ArrowTrendingUpIcon, title: 'Grow & Earn', desc: 'Optimize your content strategy and maximize revenue across all channels.', color: 'from-orange-500 to-red-500', glow: 'shadow-orange-500/30' },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.2 }}
                className="text-center relative group"
              >
                {/* Step number badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-white dark:bg-dark-800 border-2 border-gray-200 dark:border-dark-600 flex items-center justify-center z-20 shadow-sm group-hover:border-violet-400 transition-colors">
                  <span className="text-xs font-bold text-gray-500 dark:text-gray-400 group-hover:text-violet-500 transition-colors">{item.step}</span>
                </div>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} text-white flex items-center justify-center mx-auto mb-6 shadow-lg ${item.glow} relative z-10 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ───────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/30 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
              Loved by <span className="shimmer-text">Creators</span>
            </h2>
            <p className="text-lg text-gray-500 dark:text-gray-400">Join thousands of creators already transforming their business</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30, rotate: i === 0 ? -2 : i === 2 ? 2 : 0 }}
                whileInView={{ opacity: 1, y: 0, rotate: i === 0 ? -1 : i === 2 ? 1 : 0 }}
                whileHover={{ rotate: 0, y: -6, scale: 1.02 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <TiltCard className="card p-6 h-full hover:shadow-xl transition-all duration-300 group/card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => <StarSolid key={j} className="w-4 h-4 text-yellow-400" />)}
                    </div>
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${t.platformColor} bg-gray-50 dark:bg-dark-800`}>
                      <t.platformIcon className="w-3.5 h-3.5" />
                      {t.platform}
                    </div>
                  </div>

                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm leading-relaxed">
                    &ldquo;{t.quote}&rdquo;
                  </p>

                  <div className="flex items-center gap-3 mt-auto pt-4 border-t border-gray-100 dark:border-dark-700">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.gradient} flex items-center justify-center text-white font-bold text-sm shadow-md group-hover/card:shadow-lg transition-shadow`}>
                      {t.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{t.name}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{t.handle}</p>
                    </div>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3D Creator Showcase ──────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Vibrant dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-violet-950 to-indigo-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(168,85,247,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(99,102,241,0.1),transparent_50%)]" />

        {/* Floating decorative shapes */}
        <div className="absolute top-20 left-[10%] w-20 h-20 rounded-full bg-fuchsia-500/10 blur-xl float-shape" />
        <div className="absolute bottom-32 right-[15%] w-32 h-32 rounded-full bg-purple-500/10 blur-xl float-shape" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-[5%] w-16 h-16 rounded-2xl bg-pink-500/10 blur-lg float-shape" style={{ animationDelay: '4s', animationDuration: '6s' }} />
        <div className="absolute top-32 right-[8%] w-24 h-24 rounded-full bg-cyan-500/10 blur-xl float-shape" style={{ animationDelay: '1s', animationDuration: '9s' }} />
        <div className="absolute bottom-20 left-[25%] w-12 h-12 rounded-full bg-yellow-500/10 blur-lg float-shape" style={{ animationDelay: '3s', animationDuration: '7s' }} />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-white mb-4">
              One Identity, <span className="shimmer-text">Every Platform</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              A unified creator profile that represents your brand consistently wherever your audience finds you
            </p>
          </motion.div>

          {/* 3D Mini-Phone Cards */}
          <div className="showcase-perspective flex flex-col lg:flex-row justify-center items-center gap-14 lg:gap-20">
            {CREATOR_CARDS.map((creator, i) => (
              <CreatorPhoneCard key={creator.name} creator={creator} index={i} />
            ))}
          </div>

        </div>
      </section>

      {/* ── CTA Section — "command the frenzy" glow panel ──────────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center relative overflow-hidden rounded-[2rem] p-12 sm:p-16 border border-gray-200/70 dark:border-indigo-300/20 bg-white dark:bg-[#0b0d1d] shadow-2xl"
        >
          {/* Layered radial glows */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(600px 300px at 50% 0%, rgba(168,85,247,0.12), transparent 70%), radial-gradient(500px 260px at 15% 100%, rgba(236,72,153,0.10), transparent 70%), radial-gradient(500px 260px at 85% 100%, rgba(139,92,246,0.12), transparent 70%)',
            }}
          />

          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 text-sm font-medium mb-6 border border-violet-200/50 dark:border-violet-700/30"
            >
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Free 14-day trial — No credit card required
            </motion.div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-6">
              Ready to command<br /><span className="shimmer-text">the frenzy?</span>
            </h2>
            <p className="text-lg text-gray-500 dark:text-gray-400 mb-10 max-w-2xl mx-auto">
              Join 500+ creators running their entire multi-platform empire from one futuristic command deck — grow faster, earn more, work smarter.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Magnetic>
                <Link to="/register" className="group inline-flex items-center gap-2 btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white px-10 py-4 rounded-xl font-semibold text-lg shadow-xl shadow-violet-600/30 hover:shadow-violet-600/50 transition-all">
                  Create your deck
                  <RocketLaunchIcon className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Magnetic>
              <Magnetic>
                <Link to="/pricing" className="inline-block btn btn-secondary px-10 py-4 rounded-xl font-semibold text-lg transition-all">
                  See pricing
                </Link>
              </Magnetic>
            </div>
            <div className="flex flex-wrap justify-center gap-x-7 gap-y-2 mt-8">
              {['No credit card', 'Cancel anytime', 'Free forever plan'].map((b) => (
                <span key={b} className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <span className="text-green-500 font-bold">✓</span>
                  {b}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-200/50 dark:border-dark-700/50 py-12 px-4 sm:px-6 lg:px-8 bg-white/60 dark:bg-dark-900/60">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="mb-3">
                <BrandLogo size="sm" />
              </div>
              <p className="font-mono text-[9px] tracking-[0.28em] uppercase text-gray-400 dark:text-gray-500 mb-3">
                Capture<span className="text-pink-500">.</span> Connect<span className="text-purple-500">.</span> Curate<span className="text-blue-500">.</span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">AI-powered creator management for the modern internet.</p>
              <div className="flex gap-3">
                {PLATFORM_ICONS.slice(0, 4).map((p) => (
                  <a key={p.label} href="#" className="text-gray-300 dark:text-dark-600 hover:text-gray-500 dark:hover:text-gray-400 transition-colors"><p.icon className="w-4 h-4" /></a>
                ))}
              </div>
            </div>

            {[
              { title: 'Product', links: [['Pricing', '/pricing'], ['How It Works', '/how-it-works'], ['Features', '/dashboard']] },
              { title: 'Company', links: [['About Us', '/about'], ['Careers', '#'], ['Blog', '#']] },
              { title: 'Support', links: [['Help Center', '/help'], ['Contact Us', '/contact'], ['Community', '#']] },
              { title: 'Legal', links: [['Privacy Policy', '/privacy'], ['Terms of Service', '/terms'], ['Cookie Policy', '/cookies']] },
            ].map((section) => (
              <div key={section.title}>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">{section.title}</h3>
                <ul className="space-y-2">
                  {section.links.map(([label, path]) => (
                    <li key={label}>
                      <Link to={path} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">{label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-200/50 dark:border-dark-700/50 pt-8 text-center">
            <p className="flex items-center justify-center gap-2 text-sm text-gray-400 dark:text-gray-500">
              <img src={brandAsset.symbol(32)} alt="" className="w-4 h-4 opacity-80" />
              <span>&copy; 2026 OmniConnX. All rights reserved.</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Demo video lightbox */}
      <AnimatePresence>
        {demoOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDemoOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Product demo video"
          >
            <motion.div
              className="relative w-full max-w-5xl"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setDemoOpen(false)}
                aria-label="Close demo"
                className="absolute -top-11 right-0 inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium"
              >
                <XMarkIcon className="w-6 h-6" />
                Close
              </button>
              <video
                className="w-full rounded-2xl shadow-2xl bg-black aspect-video"
                src="/demo/omniconnx-demo.mp4"
                poster="/demo/omniconnx-demo-poster.jpg"
                controls
                autoPlay
                playsInline
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
